import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, gql } from '@apollo/client'
import { Box, Typography } from '@mui/material'
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded'
import ScheduleRounded from '@mui/icons-material/ScheduleRounded'
import DataTable from '../../components/DataTable'
import Header from '../../components/Headers/Header'
import CustomLoader from '../../components/Loader/CustomLoader'
import TableHeader from '../../components/TableHeader'
import SearchBar from '../../components/TableHeader/SearchBar'
import { influencerSidebarIcons } from '../../components/Sidebar/sidebarIcons'
import { getInfluencerOrders } from '../../apollo'
import { getViewingInfluencerId } from '../../utils/influencerContext'
import { customStyles } from '../../utils/tableCustomStyles'
import { StatusPill, money } from './shared'

const GET_ORDERS = gql`
  ${getInfluencerOrders}
`

const PAGE_SIZE = 20

// Deliberately thin: this is proof the coupon was used and what it earned,
// never who placed the order or what they ordered. No customer, no order
// amount, no pickup/delivery, no raw fulfillment status -- the API itself
// doesn't send any of that here, this isn't just a hidden column.
function InfluencerOrders(props) {
  const { t } = props
  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [search, setSearch] = useState('')

  const influencerId = getViewingInfluencerId()
  const { data, loading, error, refetch } = useQuery(GET_ORDERS, {
    variables: {
      offset: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      search: search || null,
      influencerId
    },
    fetchPolicy: 'network-only'
  })

  const runSearch = () => {
    setPage(1)
    setSearch(searchQuery)
    refetch()
  }

  const columns = [
    {
      name: t('OrderID'),
      selector: 'orderId',
      sortable: true,
      cell: row => (
        <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
          {row.orderId}
        </Typography>
      )
    },
    { name: t('Coupon'), selector: 'couponTitle' },
    {
      name: t('Earning'),
      cell: row => (
        <Typography
          sx={{ fontWeight: 700, fontSize: 13, color: 'success.main' }}>
          {money(row.earning)}
        </Typography>
      )
    },
    {
      name: t('Status'),
      cell: row =>
        row.isEarning ? (
          <StatusPill tone="green" icon={CheckCircleRounded}>
            {t('Paid')}
          </StatusPill>
        ) : (
          <StatusPill tone="amber" icon={ScheduleRounded}>
            {t('PendingCompletion')}
          </StatusPill>
        )
    },
    {
      name: t('Date'),
      cell: row => (
        <>{row.createdAt ? new Date(row.createdAt).toDateString() : '-'}</>
      )
    }
  ]

  const result = data?.influencerOrders

  return (
    <>
      <Header
        title={t('Orders')}
        subtitle={t('SubtitleInfluencerOrders')}
        icon={influencerSidebarIcons.Orders}
      />
      {error ? (
        <Typography sx={{ color: 'error.main', mb: 2 }}>
          {error.message}
        </Typography>
      ) : null}
      {loading && !result ? (
        <CustomLoader />
      ) : (
        <DataTable
          subHeader={true}
          subHeaderComponent={
            <SearchBar
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onClick={runSearch}
            />
          }
          title={<TableHeader title={t('MyOrders')} />}
          columns={columns}
          data={result?.orders || []}
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={<CustomLoader />}
          pagination
          paginationServer
          paginationTotalRows={result?.totalCount || 0}
          paginationPerPage={PAGE_SIZE}
          paginationRowsPerPageOptions={[PAGE_SIZE]}
          onChangePage={setPage}
          noDataComponent={
            <Box p={3}>
              <Typography sx={{ color: 'text.disabled' }}>
                {t('NoOrdersYet')}
              </Typography>
            </Box>
          }
        />
      )}
    </>
  )
}

export default withTranslation()(InfluencerOrders)
